import React, { useEffect, useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Button, MenuItem, Menu, Divider, Drawer } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { STATUS } from '../../utils/status';
import { createMultipleExpert, getExpert, setCreateMultipleExpertsStatus, setUpdateExpertsStatus, updateExpert } from '../../redux/slices/expertSlice/addExpertSlice';
import { useNavigate } from 'react-router-dom';
import { TableCellHead } from '../table/tableCell';
import DeleteModal from '../modal/deleteModal';
import DeboardUserModal from '../modal/deboardUserModal';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SnackbarNotification from '../snackbar/snackbarNotification';
import DisableModal from './disableModal';
import PaginationEntries from '../pagination/paginationEntries';
import Pagination from './expertPagination';
import { SearchBar } from '../inputField/textfield';
import { useStyles } from '../../assets/styles/listTableStyle';
import TableRowItem from './expertTableRowItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Error from '../error/error';
import Groups2Icon from '@mui/icons-material/Groups2';
import debounce from 'lodash.debounce';
import InviteEmailsDrawer from './inviteEmailsDrawer';
import ArrowButton from '../ArrowButton/arrowButton';
import { constants } from '../../constants/env';

const ExpertListTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn] = useState("name");
  const [sortOrder] = useState("asc");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [expertToDisable, setExpertToDisable] = useState<any>(null);

  const experts = useSelector((state: RootState) => state.experts.experts);
  const createMultipleExpertMessage = useSelector((state: RootState) => state.experts.createMultipleExpertMessage)
  const updateMultipleExpertMessage = useSelector((state: RootState) => state.experts.updateExpertMessage)
  const getStatus = useSelector((state: RootState) => state.experts.getStatus);
  const createStatus = useSelector((state: RootState) => state.experts.createStatus);
  const createMultipleStatus = useSelector((state: RootState) => state.experts.createMultipleStatus);
  const expertTableAccess = useSelector((state: RootState) => state.company.company.data.modules.experts.table.access);
  const searchAccess = useSelector((state: RootState) => state.company.company.data.modules.experts.table.search.access);
  const addAccess = useSelector((state: RootState) => state.company?.company?.data?.actions?.create);
  const tableFields = useSelector((state: RootState) => {
    const tableFields = state.company?.company?.data?.modules?.experts?.table?.table_fields;
    return tableFields || [];
  });
  const updateStatus = useSelector((state: RootState) => state.experts.updateStatus);
  const isActionEnabled = constants.REACT_APP_ACTIONS_ENABLED;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.trim());
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleSearchChange, 300);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteClick = (userId: string) => {
    if (!isActionEnabled) return;
    setUserIdToDelete(userId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    dispatch(updateExpert({ id: userIdToDelete, is_deleted: true }));
  };

  useEffect(() => {
    dispatch(getExpert({
      search: searchTerm,
      page: currentPage,
      pageSize: itemsPerPage,
    })
    );
  }, [dispatch, searchTerm, currentPage, sortColumn, sortOrder, itemsPerPage]);

  const handleAddClick = () => {
    navigate("/addexpert");
  };

  const handleEditClick = (row: any) => {
    if (!isActionEnabled) return;
    navigate(`/addexpert/${row.id}`, {
      state: { expertDetails: row, id: row.id },
    });
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleChangeRowsPerPage = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
    dispatch(getExpert({ search: searchTerm, page: 1, pageSize: value as number }));
  };
  const handleOnChange = (value: number) => {
    setItemsPerPage(value)
  }

  const capitalizeAll = (str: string) => {
    return str
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.toUpperCase())
      .join(" ");
  };

  const headers = tableFields.map(capitalizeAll);

  const handleInviteClick = () => {
    setInviteModalOpen(true);
  };

  const validateEmails = (emails: string) => {
    console.log({ emails })
    const emailArray = emails.split(',').map(email => email.trim());
    const validEmailRegex = /^(?!.*\.\.)(?!.*\.$)(?!^\.)[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const invalidEmails = emailArray.filter(
      (email) => !validEmailRegex.test(email)
    );
    if (invalidEmails.length > 0) {
      return `Invalid emails ${invalidEmails.join(", ")}`;
    }

    const duplicateEmails = emailArray.filter(
      (email, index) => emailArray.indexOf(email) !== index
    );
    if (duplicateEmails.length > 0) {
      return `Duplicate emails: ${duplicateEmails.join(", ")}`;
    }

    return null;
  };

  useEffect(() => {
    if (createMultipleStatus === STATUS.SUCESS) {
      handleSnackbar(createMultipleExpertMessage, "success")
    }
    if (createMultipleStatus === STATUS.FAILED) {
      handleSnackbar(createMultipleExpertMessage, "error")
    }

    if (createMultipleStatus === STATUS.SUCESS || createMultipleStatus === STATUS.FAILED) {
      dispatch(setCreateMultipleExpertsStatus({}))
    }
  })

  const handleAddAllClick = () => {
    const errorMessage = validateEmails(inviteEmails);
    if (errorMessage) {
      handleSnackbar(errorMessage, "error");
      return;
    }
    const payload = { emails: inviteEmails };
    dispatch(createMultipleExpert(payload))
      .then(() => {
        setInviteEmails("");
        setInviteModalOpen(false);
        // handleSnackbar("Invitations sent successfully", "success");
      })
      .catch(() => {
        // handleSnackbar(createMultipleExpertMessage, "error");
      });
  };

  useEffect(() => {
    if (createMultipleStatus === STATUS.SUCESS) {
      handleSnackbar(createMultipleExpertMessage, "success");
      setTimeout(() => {
        navigate("/experts");
      }, 1000);
      dispatch(setCreateMultipleExpertsStatus(STATUS.NOT_STARTED));
      dispatch(getExpert({}));
      setInviteModalOpen(false)
    }

    if (createMultipleStatus === STATUS.FAILED) {
      handleSnackbar(createMultipleExpertMessage, "error");
      setTimeout(() => {
        handleSnackbar(createMultipleExpertMessage, "warning");
      }, 7000);

      dispatch(setCreateMultipleExpertsStatus(STATUS.NOT_STARTED));
    }
  }, [createMultipleStatus]);

  const handleDisableCancel = () => {
    setShowDisableModal(false);
    setExpertToDisable(null);
  };


  useEffect(() => {
    if (createStatus === STATUS.SUCESS) {
      handleSnackbar(updateMultipleExpertMessage, "success")
    }
    if (createStatus === STATUS.FAILED) {
      handleSnackbar(updateMultipleExpertMessage, "error")
    }

    if (createStatus === STATUS.SUCESS || createStatus === STATUS.FAILED) {
      dispatch(setUpdateExpertsStatus({}))
    }
  }, [dispatch, createStatus])


  const handleDisableConfirm = () => {
    // setShowDisableModal(false);
    if (expertToDisable) {
      const updatedStatus = !expertToDisable.is_blocked;
      try {
        dispatch(updateExpert({ id: expertToDisable.id, is_blocked: updatedStatus }));
        dispatch(getExpert({}));
        // handleSnackbar(updateMultipleExpertMessage, 'success');
      } catch (error) {
        // handleSnackbar(updateMultipleExpertMessage, "error");
      }
    }
    setExpertToDisable(null);
  };

  const handleToggleDisable = (row: any) => {
    if (!isActionEnabled) return;
    setExpertToDisable(row);
    setShowDisableModal(true);
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (updateStatus === STATUS.SUCESS) {
      setShowDeleteModal(false)
      dispatch(getExpert({}));
      handleSnackbar(updateMultipleExpertMessage, 'success');

    }
    if (updateStatus === STATUS.FAILED) {
      setShowDeleteModal(false)
      dispatch(getExpert({}));
      handleSnackbar(updateMultipleExpertMessage, 'error');
    }
    return () => {
      if (updateStatus === STATUS.SUCESS || updateStatus === STATUS.FAILED) {
        dispatch(setUpdateExpertsStatus(STATUS.NOT_STARTED));
      }
    };
  }, [updateStatus, navigate, dispatch]);

  useEffect(() => {
    if (updateStatus == STATUS.SUCESS) {
      setShowDeleteModal(false);
    }
  }, [updateStatus])

  useEffect(() => {
    if (updateStatus == STATUS.SUCESS) {
      setShowDisableModal(false)
    }
  }, [updateStatus])

  const totalPages = Math.ceil((experts.count || 0) / itemsPerPage);
  return (
    <>

      {expertTableAccess && (
        <>
          <div className={classes.container}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <div style={{ marginLeft: "0px" }}>{searchAccess && (<SearchBar onSearchChange={debouncedResults} />)}</div>
            </div>

            <div className={classes.actionsContainer}>
              <PaginationEntries itemsPerPage={itemsPerPage} onChangeRowsPerPage={handleChangeRowsPerPage} handleOnChange={handleOnChange} />
              {addAccess && constants.CREATE_PROJECT_FEATURE &&  (
                <>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={handleInviteClick}
                    sx={{
                      width: 'auto',
                      height: '40px',
                      marginTop: '10px',
                      borderRadius: '8 0 0 8',
                      padding: '8px, 16px, 8px, 16px',
                      backgroundColor: '#3D85C6',
                      textTransform: 'none',
                      marginLeft: '10px',
                      '&:hover': {
                        backgroundColor: '#3D85C6',
                      },
                    }}>
                    <Groups2Icon fontSize='small' sx={{ marginRight: '10px' }} /> Add Bulk Expert
                  </Button>

                  <ArrowButton handleMenuClick={handleMenuClick} anchorEl={anchorEl} handleMenuClose={handleMenuClose} handleAddClick={handleAddClick} menuText={"Add Expert"} />

                  {/* <Divider orientation="vertical" flexItem sx={{ height: '1px' }} />
                  <ArrowDropDownIcon fontSize='small' onClick={handleMenuClick} sx={{
                    width: '30px',
                    height: '40px',
                    marginTop: '10px',
                    color: 'white',
                    borderRadius: '0 8px 8px 0',
                    padding: '8px, 16px, 8px, 16px',
                    backgroundColor: '#3D85C6',
                    '&:hover': {
                      backgroundColor: '#3D85C6',
                    },
                  }} />
                  <Menu
                    id="invite-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={handleAddClick}><PersonAddAlt1Icon fontSize='small' sx={{ marginRight: '5px' }} />Add Expert</MenuItem>
                  </Menu> */}
                </>)}
            </div>
          </div>

          <Drawer
            anchor="right"
            open={inviteModalOpen}
            onClose={() => setInviteModalOpen(false)}
            sx={{
              '& .MuiDrawer-paper': {
                width: '40%',
                padding: '40px',
              }
            }}
          >
            <InviteEmailsDrawer handleAddAllClick={handleAddAllClick} inviteEmails={inviteEmails} setInviteEmails={setInviteEmails} handleClose={() => setInviteModalOpen(false)} />
          </Drawer>



          <TableContainer style={{ minHeight: '75px' }}>
            {getStatus == STATUS.PENDING && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <CircularProgress />
              </div>
            )}
            {getStatus == STATUS.SUCESS && (
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead sx={{ backgroundColor: '#F7F9FA', '&:first-child td, &:first-child th': { borderBottom: 0 } }}>
                  <TableRow>
                    {headers.map((header : any) => (
                      <TableCellHead key={header}>{header}</TableCellHead>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getStatus === STATUS.SUCESS && experts && experts.results && experts.results.length > 0 ? (
                    experts.results.map((row: any) => (
                      <TableRowItem
                        key={row.id}
                        row={row}
                        onEditClick={handleEditClick}
                        onDeleteClick={handleDeleteClick}
                        onToggleDisable={handleToggleDisable}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className={classes.noUsersMessage}>
                        <Error message={'There are no experts added as of yet. You can add a expert to see it here.'} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
            <DeboardUserModal
              open={showDeleteModal}
              onCancel={cancelDelete}
              onConfirm={confirmDelete}
            />
            <DisableModal
              open={showDisableModal}
              onCancel={handleDisableCancel}
              onConfirm={handleDisableConfirm}
              isDisabled={expertToDisable ? expertToDisable.is_blocked : false}
            />
          </TableContainer>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            paginate={paginate}
            itemsPerPage={itemsPerPage}
          />
          <SnackbarNotification
            open={snackbarOpen}
            message={snackbarMessage}
            severity={snackbarSeverity}
            onClose={handleSnackbarClose}
          />
        </>
      )}
    </>
  );
};

export default ExpertListTable;
